import * as React from 'react';
import { Button, Card, Collapse } from 'reactstrap';
import { BtnSelect, IBtnSelectOption } from '../../ui/BtnSelect';
import { HttpRequest } from '../../../util/http';
import { ContentCardHeader } from '../../ui/ContentCardHeader';
import { DataGrid, DataGridSortOrder } from '../../ui/DataGrid';
import { FontAwesomeIcon } from '../../ui/FontAwesomeIcon';
import { GetAllAvids } from '../../util';
import { AvidEditor } from '../AvidEditor';
import { IAvid } from '../interfaces';

interface IAvidsDataGridProps {
  reloadAvidsFn?: (loadAvidsFn: () => Promise<void>) => void
}

interface IAvidsDataGridState {
  avids: IAvid[]
  selectedAvidIds: string[]
  sortOrder: DataGridSortOrder
  sortBy: keyof IAvid
  disableActions: boolean
  avidInEditMode?: IAvid
}

export class AvidsDataGrid extends React.Component<IAvidsDataGridProps, IAvidsDataGridState> {
  state: IAvidsDataGridState = {
    avids: [],
    selectedAvidIds: [],
    sortOrder: 'desc',
    sortBy: 'avid',
    disableActions: false,
  }

  componentWillMount() {
    this.loadAvids()

    if (this.props.reloadAvidsFn) {
      this.props.reloadAvidsFn(this.loadAvids.bind(this))
    }
  }

  deleteAvid(avidContainer: IAvid, reloadAvids = true, confirmDelete = true) {
    const { disableActions } = this.state
    const { avid } = avidContainer
    const data = { avid }
    const method = 'POST'
    const endpoint = '/scripts/avids/avids_delete'

    if (disableActions) { return }

    if (confirmDelete) {
      const confirmDeletion = window.confirm(`Are you sure you want to delete "${avid}" response?`)

      if (!confirmDeletion) { return }
    }

    // Remove from dataset
    const { avids, selectedAvidIds } = this.state
    const deletingAvidIndex = avids.findIndex((a) => a.avid === avid)
    const selectedAvidIndex = selectedAvidIds.indexOf(avid)

    avids.splice(deletingAvidIndex, 1)

    if (selectedAvidIndex >= 0) {
      selectedAvidIds.splice(selectedAvidIndex, 1)
    }

    this.setState({ avids, selectedAvidIds, disableActions: true })

    const deletePromise = HttpRequest.fetch(endpoint, { data, method })

    if (reloadAvids) {
      deletePromise.finally(() => this.loadAvids())
    }

    return deletePromise
  }

  bulkDelete() {
    const { selectedAvidIds, avids } = this.state
    const selectedAvidsLength = selectedAvidIds.length
    const confirmDelete = confirm(`Are you sure you want to delete ${selectedAvidsLength} Avids?`)

    if (!confirmDelete) { return; }

    this.setState({ disableActions: true })

    const selectedAvidContainers = avids.filter((avid) => selectedAvidIds.indexOf(avid.avid) >= 0)
    const deletePromises = selectedAvidContainers.map((avid) => this.deleteAvid(avid, false, false))

    Promise.all(deletePromises).finally(() => this.loadAvids())
  }

  loadAvids() {
    this.setState({ disableActions: true })

    return GetAllAvids()
      .then((avids) => this.setState({ avids }))
      .finally(() => this.setState({ disableActions: false }))
  }

  toggleSelectedAvids(avid: IAvid) {
    const { selectedAvidIds } = this.state
    const selectedIndex = selectedAvidIds.indexOf(avid.avid)

    if (selectedIndex >= 0) {
      selectedAvidIds.splice(selectedIndex, 1)
    } else {
      selectedAvidIds.push(avid.avid)
    }

    this.setState({ selectedAvidIds })
  }

  isSelected(avid: IAvid) {
    const { selectedAvidIds } = this.state
    const selectedIndex = selectedAvidIds.indexOf(avid.avid)

    return selectedIndex >= 0
  }

  avidClicked(avidInEditMode: IAvid) {
    const currentShownAvid = this.state.avidInEditMode
    const shouldToggle = currentShownAvid && avidInEditMode.avid === currentShownAvid.avid
    avidInEditMode = shouldToggle ? undefined : avidInEditMode

    this.setState({ avidInEditMode })
  }

  removeShownAvid(reloadAvids = false) {
    if (reloadAvids) {
      return this.loadAvids().then(() => this.setState({ avidInEditMode: undefined }))
    } else {
      this.setState({ avidInEditMode: undefined })
    }
  }

  getParentIdLabel(avidContainer: IAvid) {
    const { avids } = this.state
    const { parentid } = avidContainer
    const parent = avids.find((a) => a.avid === parentid)

    return parent ? parent.label : parentid
  }

  async updateParentIds(parentid: string) {
    const { selectedAvidIds, avids } = this.state
    const selectedAvids = avids.filter((a) => selectedAvidIds.indexOf(a.avid) >= 0)

    this.setState({ disableActions: true })

    for (const selectedAvid of selectedAvids) {
      const method = 'POST'
      const data = {
        avid: selectedAvid.avid,
        cientid: selectedAvid.clientsid,
        label: selectedAvid.label,
        parentid,
        hierarchylevel: selectedAvid.hierarchylevel,
        dynamicChildren: selectedAvid.dynamicChildren,
        childInput: selectedAvid.childInputIdField,
        childLabel: selectedAvid.childLabelIdField
      }

      //must do fetches in sequence otherwise DB lock occurs
      await HttpRequest.fetch('/scripts/avids/avids_modify', { data, method })
    }

    await this.loadAvids()

    this.setState({ selectedAvidIds: [], disableActions: false })
  }

  renderBulkCheck(avidContainer: IAvid) {
    const { disableActions } = this.state
    const isSelected = this.isSelected(avidContainer)
    const isNotSelected = !isSelected
    const icon = isSelected ? 'check-square' : 'square'
    const className = (isNotSelected || disableActions) ? 'text-dark' : ''
    const iconElement = (
      <FontAwesomeIcon className={className}
        verticalAlign="middle"
        icon={icon}
        regular={isNotSelected && !disableActions} />
    )

    if (disableActions) {
      return (<span>{iconElement}</span>)
    }

    return (
      <a href="javascript:void(0)" onClick={() => this.toggleSelectedAvids(avidContainer)}>
        {iconElement}
      </a>
    )
  }

  render() {
    const { selectedAvidIds, avidInEditMode, disableActions, avids } = this.state
    const selectedAvidsLength = selectedAvidIds.length
    const hasSelectedAvids = selectedAvidsLength > 0
    const shouldDisableBulkActions = !hasSelectedAvids || disableActions
    const avidSelects: IBtnSelectOption[] = avids.map(({ label, avid: value }) => ({ value, label }))

    avidSelects.sort((a1, a2) => a1.label.localeCompare(a2.label))

    return (
      <Card>
        <ContentCardHeader sticky borderBottom loading={disableActions}
          header={
            <>
              <span>Avids</span>
              {hasSelectedAvids &&
                <small>:&nbsp;{selectedAvidsLength.toLocaleString()}&nbsp;selected</small>
              }
            </>
          }
          headerActions={
            <section className="mt-2">
              <BtnSelect header="Bulk Update Parent Avid"
                color="primary"
                clearLabel="Cancel"
                options={avidSelects}
                disabled={shouldDisableBulkActions}
                onChange={(parentId) => this.updateParentIds(parentId)}
                clearable
                persistLabel
                disableBoldHeader />
              <Button className="ml-2" color="primary"
                disabled={shouldDisableBulkActions}
                onClick={() => this.bulkDelete()}>
                Bulk Delete
                </Button>
              <Button className="ml-2" color="primary"
                disabled={shouldDisableBulkActions}
                onClick={() => this.setState({ selectedAvidIds: [] })}>
                Clear Selection
                </Button>
            </section>
          } />
        <DataGrid<IAvid>
          records={this.state.avids}
          sortBy={this.state.sortBy}
          sortOrder={this.state.sortOrder}
          onSortChange={(sortBy, sortOrder) => this.setState({ sortBy, sortOrder })}
          showSubComponent={(avid) => avidInEditMode && avid.avid === avidInEditMode.avid}
          subComponent={() =>
            <AvidEditor
              editMode={avidInEditMode}
              onSuccess={() => this.removeShownAvid(true)}
              onCancel={() => this.removeShownAvid()} />
          }
          columns={[
            { header: 'Bulk', align: 'center', sortable: false, render: (avid) => this.renderBulkCheck(avid) },
            {
              header: 'Avid', property: 'avid', render: (avid) => (
                <>
                  {disableActions ?
                    <span>{avid.avid}</span>
                    :
                    <a href="javascript:void(0)"
                      onClick={() => this.avidClicked(avid)}>
                      <FontAwesomeIcon icon="edit" verticalAlign="initial" size="md" />
                      <span>&nbsp;{avid.avid}</span>
                    </a>
                  }
                </>
              )
            },
            { header: 'Client Id', property: 'clientsid' },
            { header: 'Label', property: 'label' },
            {
              header: 'Parent Avid', render: (avid) =>
                <span>{this.getParentIdLabel(avid)}</span>
            },
            { header: 'Hierarchy Level', property: 'hierarchylevel' },
            {
              header: 'Delete', align: 'center', sortable: false, render: (avid) => (
                <a href="javascript:void(0)"
                  onClick={() => this.deleteAvid(avid)}>
                  <FontAwesomeIcon icon="trash-alt" size="md" verticalAlign="middle" muted={disableActions} />
                </a>
              )
            },
          ]} />
      </Card>
    )
  }
}
